import React from "react";
import SectionRows from "../../shared/SectionRows";
import { formatAmount } from "../../shared/helpers/formatters";
import { CURRENT_ASSET_CATS, NONCURRENT_ASSET_CATS, CURRENT_LIAB_CATS, LT_LIAB_CATS, EQUITY_CATS } from "../../shared/helpers/constants";
import "../../afs-walkthrough.css";
import WComponent from "../../../../../common/WComponent";

const fmt = formatAmount;

class BSView extends WComponent {
	state = { expanded: new Set() };

	toggle = name => {
		this.setState(prev => { const n = new Set(prev.expanded); n.has(name) ? n.delete(name) : n.add(name); return { expanded: n }; });
	};

	render() {
	let { balanceSheet, currentYear, priorYear, priorPriorYear, showComparatives, show2022, hasTransactions, onDrillAccount, onNavigateToLeadSheet } = this.props;
	let { expanded } = this.state;
	const toggle = this.toggle;

	const sections = balanceSheet.sections || [];
	const cols = 2 + (showComparatives ? 1 : 0) + (show2022 ? 1 : 0);

	const currentAssets    = sections.filter(s => CURRENT_ASSET_CATS.has(s.category));
	const nonCurrentAssets = sections.filter(s => NONCURRENT_ASSET_CATS.has(s.category));
	const currentLiab     = sections.filter(s => CURRENT_LIAB_CATS.has(s.category));
	const ltLiab          = sections.filter(s => LT_LIAB_CATS.has(s.category));
	const equitySections  = sections.filter(s => EQUITY_CATS.has(s.category));

	const subtotal = (secs, field) => secs.reduce((sum, s) => sum + (s[field] || 0), 0);

	const sharedProps = { expanded, onToggle: toggle, showComparatives, show2022, hasTransactions, onDrillAccount, onNavigateToLeadSheet };

	return (
		<table className="afs-fs-table">
			<thead>
				<tr>
					<th className="afs-fs-table__th-name" />
					<th className="afs-fs-table__th-wp">WP</th>
					<th className="afs-fs-table__th-amt">{currentYear}</th>
					{showComparatives && <th className="afs-fs-table__th-amt">{priorYear}</th>}
					{show2022 && <th className="afs-fs-table__th-amt afs-fs-table__th-amt--faded">{priorPriorYear}</th>}
				</tr>
			</thead>
			<tbody>
				{/* ASSETS */}
				<tr><td colSpan={cols} className="afs-fs-table__group-header">Assets</td></tr>

				{/* Current Assets — with "CURRENT" subheader and subtotal */}
				{currentAssets.length > 0 && (
					<>
						<tr><td colSpan={cols} className="afs-fs-table__subgroup-header">Current</td></tr>
						{currentAssets.map(s => <SectionRows key={s.name} section={s} {...sharedProps} />)}
						<tr className="afs-fs-table__subtotal-row">
							<td className="afs-fs-table__subtotal-name">Total Current Assets</td>
							<td /><td className="afs-fs-table__subtotal-amt">{fmt(subtotal(currentAssets, "totalCurrent"))}</td>
							{showComparatives && <td className="afs-fs-table__subtotal-amt">{fmt(subtotal(currentAssets, "totalPrior"))}</td>}
							{show2022 && <td className="afs-fs-table__subtotal-amt afs-fs-table__subtotal-amt--faded">{fmt(subtotal(currentAssets, "totalPriorPrior"))}</td>}
						</tr>
					</>
				)}

				{/* Non-current Assets — same SectionRows component, consistent formatting */}
				{nonCurrentAssets.map(s => <SectionRows key={s.name} section={s} {...sharedProps} />)}

				<tr className="afs-fs-table__grand-total-row">
					<td className="afs-fs-table__grand-total-name">Total Assets</td>
					<td /><td className="afs-fs-table__grand-total-amt">{fmt(balanceSheet.totalAssets?.current)}</td>
					{showComparatives && <td className="afs-fs-table__grand-total-amt">{fmt(balanceSheet.totalAssets?.prior)}</td>}
					{show2022 && <td className="afs-fs-table__grand-total-amt afs-fs-table__grand-total-amt--faded">{fmt(balanceSheet.totalAssets?.priorPrior)}</td>}
				</tr>

				{/* spacer */}
				<tr><td colSpan={cols} className="afs-fs-table__spacer" /></tr>

				{/* LIABILITIES AND EQUITY */}
				<tr><td colSpan={cols} className="afs-fs-table__group-header">Liabilities and Shareholders' Equity</td></tr>

				{currentLiab.length > 0 && (
					<>
						<tr><td colSpan={cols} className="afs-fs-table__subgroup-header">Current Liabilities</td></tr>
						{currentLiab.map(s => <SectionRows key={s.name} section={s} {...sharedProps} />)}
						<tr className="afs-fs-table__subtotal-row">
							<td className="afs-fs-table__subtotal-name">Total Current Liabilities</td>
							<td /><td className="afs-fs-table__subtotal-amt">{fmt(subtotal(currentLiab, "totalCurrent"))}</td>
							{showComparatives && <td className="afs-fs-table__subtotal-amt">{fmt(subtotal(currentLiab, "totalPrior"))}</td>}
							{show2022 && <td className="afs-fs-table__subtotal-amt afs-fs-table__subtotal-amt--faded">{fmt(subtotal(currentLiab, "totalPriorPrior"))}</td>}
						</tr>
					</>
				)}

				{ltLiab.length > 0 && (
					<>
						<tr><td colSpan={cols} className="afs-fs-table__subgroup-header">Long-term</td></tr>
						{ltLiab.map(s => <SectionRows key={s.name} section={s} {...sharedProps} />)}
						<tr className="afs-fs-table__subtotal-row">
							<td className="afs-fs-table__subtotal-name">Total Long-term</td>
							<td /><td className="afs-fs-table__subtotal-amt">{fmt(subtotal(ltLiab, "totalCurrent"))}</td>
							{showComparatives && <td className="afs-fs-table__subtotal-amt">{fmt(subtotal(ltLiab, "totalPrior"))}</td>}
							{show2022 && <td className="afs-fs-table__subtotal-amt afs-fs-table__subtotal-amt--faded">{fmt(subtotal(ltLiab, "totalPriorPrior"))}</td>}
						</tr>
					</>
				)}

				{equitySections.length > 0 && (
					<>
						<tr><td colSpan={cols} className="afs-fs-table__subgroup-header">Equity</td></tr>
						{equitySections.map(s => <SectionRows key={s.name} section={s} {...sharedProps} />)}
						<tr className="afs-fs-table__subtotal-row">
							<td className="afs-fs-table__subtotal-name">Total Equity</td>
							<td /><td className="afs-fs-table__subtotal-amt">{fmt(subtotal(equitySections, "totalCurrent"))}</td>
							{showComparatives && <td className="afs-fs-table__subtotal-amt">{fmt(subtotal(equitySections, "totalPrior"))}</td>}
							{show2022 && <td className="afs-fs-table__subtotal-amt afs-fs-table__subtotal-amt--faded">{fmt(subtotal(equitySections, "totalPriorPrior"))}</td>}
						</tr>
					</>
				)}

				<tr className="afs-fs-table__grand-total-row">
					<td className="afs-fs-table__grand-total-name">Total Liabilities and Equity</td>
					<td /><td className="afs-fs-table__grand-total-amt">{fmt(balanceSheet.totalLiabilitiesAndEquity?.current)}</td>
					{showComparatives && <td className="afs-fs-table__grand-total-amt">{fmt(balanceSheet.totalLiabilitiesAndEquity?.prior)}</td>}
					{show2022 && <td className="afs-fs-table__grand-total-amt afs-fs-table__grand-total-amt--faded">{fmt(balanceSheet.totalLiabilitiesAndEquity?.priorPrior)}</td>}
				</tr>
				{(() => {
					let totalA = balanceSheet.totalAssets?.current || 0;
					let totalLE = balanceSheet.totalLiabilitiesAndEquity?.current || 0;
					let diff = Math.round(Math.abs(totalA - totalLE));
					if (diff > 0) {
						return (
							<tr>
								<td colSpan={cols} className="afs-fs-table__imbalance">
									Out of balance by {fmt(diff)} (Assets {fmt(totalA)} vs L&E {fmt(totalLE)})
								</td>
							</tr>
						);
					}
					return null;
				})()}
			</tbody>
		</table>
	);
	}
}

export default BSView;
